import React, { useId, useState } from 'react'
import { PROJECT } from 'app-constants'
import { IProjectSecret } from 'types'
import Button from 'components/button'
import Dialog from 'components/dialog'
import Field from 'components/field'
import Input from 'components/input'
import { BLUE_BUTTON } from 'components/save-buttons'

export interface ProjectSecretFormValues {
  name: string
  value: string
}

// A name scripts can read it by: a letter, then letters, numbers or underscores.
const VALID_NAME = /^[A-Za-z]\w*$/

const LABEL = 'text-[13px] font-medium text-body'
const FIELD = 'flex flex-col gap-1.5'
const HINT = 'text-xs leading-4 text-muted'

interface SecretDialogProps {
  /** The secret to edit: its name (fixed for the webhook signing secret) and a new value. Without one, adds one. */
  secret?: IProjectSecret
  onClose: () => void
  /** Saves it; the caller closes the dialog when it's saved, and says why when it isn't. */
  onSubmit: (values: ProjectSecretFormValues) => void | Promise<void>
}

/**
 * Adds a secret, or gives one a new value, in our dialog: its name and its value, each saying what's
 * allowed, and Add Secret or Update Secret once both are valid.
 */
const SecretDialog: React.FC<SecretDialogProps> = ({ secret, onClose, onSubmit }) => {
  const id = useId()
  const [name, setName] = useState(secret?.name ?? '')
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // A secret's own name is kept as it is, even one saved before names had to start with a letter.
  const nameValid = VALID_NAME.test(name) || (!!secret && name === secret.name)
  const nameInvalid = name !== '' && !nameValid
  const ready = nameValid && value !== ''

  const submit = async () => {
    setSubmitting(true)
    await onSubmit({ name, value })
    setSubmitting(false)
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={secret ? 'Edit Secret' : 'Add Secret'}
      description={
        secret
          ? `Saving replaces the current value. Scripts in this ${PROJECT} will use the new value.`
          : `Scripts in this ${PROJECT} can read the secret by its name. The value is hidden once saved.`
      }
      footer={
        <>
          <Button internalType="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            internalType="outline"
            className={BLUE_BUTTON}
            disabled={!ready}
            isLoading={submitting}
            onClick={submit}
          >
            {secret ? 'Update Secret' : 'Add Secret'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field label="Name" htmlFor={`${id}-name`} className={FIELD} labelClassName={LABEL}>
          <Input
            id={`${id}-name`}
            placeholder="my_api_key"
            autoComplete="off"
            value={name}
            disabled={secret?.isReadOnly}
            aria-invalid={nameInvalid || undefined}
            aria-describedby={`${id}-name-hint`}
            onChange={(event) => setName(event.target.value)}
          />
          <p id={`${id}-name-hint`} className={nameInvalid ? `${HINT} text-red` : HINT}>
            {secret?.isReadOnly
              ? "The webhook signing secret's name cannot be changed."
              : 'Starts with a letter. Letters, numbers and underscores only.'}
          </p>
        </Field>
        <Field label={secret ? 'New value' : 'Value'} htmlFor={`${id}-value`} className={FIELD} labelClassName={LABEL}>
          <Input
            id={`${id}-value`}
            autoComplete="off"
            spellCheck={false}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  )
}

export default SecretDialog
